/**
 * CryptRightPseudoBox Component
 *
 * Crypt skin right pseudo box with red/orange gradient surface, bone and candle
 * decorative elements, and ornate gold/bronze frame. Displays player username with truncation.
 */

import { memo } from 'react';
import type { PseudoBoxProps } from '../basics/constants/types';
import { buildClassNames } from '@/utils/classNames';
import { formatPseudoForDisplay, PSEUDO_BOX_CONFIG } from '../basics/utils/utils';
import { CRYPT_PSEUDO_VIEWBOX } from './constants';

const CryptRightPseudoBox = memo(function CryptRightPseudoBox({
  pseudo,
  viewBox = CRYPT_PSEUDO_VIEWBOX,
  className = ''
}: PseudoBoxProps) {
  const svgClassName = buildClassNames(className);
  const displayText = formatPseudoForDisplay(pseudo);

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Player username display box - crypt right position"
      focusable="false"
    >
      <defs>
        {/* Dark bottom base */}
        <linearGradient
          id="crpRDark"
          x1="5.3198"
          y1="72.05"
          x2="302.73"
          y2="72.05"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#2F3806" />
          <stop offset="1" stopColor="#393D04" />
        </linearGradient>
        {/* Green mid-layer accent */}
        <linearGradient
          id="crpRGreen"
          x1="154.04"
          y1="103.24"
          x2="154.04"
          y2="74.6301"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#95A14F" />
          <stop offset="1" stopColor="#6E7645" />
        </linearGradient>
        {/* Red/orange surface gradient - full width */}
        <linearGradient
          id="crpRSurface"
          x1="301.05"
          y1="42.7599"
          x2="7.79982"
          y2="42.7599"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ED1C24" />
          <stop offset="0.24" stopColor="#C1272D" />
          <stop offset="0.47" stopColor="#F7931E" />
          <stop offset="0.75" stopColor="#ED1C24" />
          <stop offset="1" stopColor="#F15A24" />
        </linearGradient>
        {/* Red/orange surface gradient - left half */}
        <linearGradient
          id="crpRSurfaceLeft"
          x1="5.31982"
          y1="42.7599"
          x2="173.66"
          y2="42.7599"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ED1C24" />
          <stop offset="0.24" stopColor="#C1272D" />
          <stop offset="0.47" stopColor="#F7931E" />
          <stop offset="0.75" stopColor="#ED1C24" />
          <stop offset="1" stopColor="#F15A24" />
        </linearGradient>
        {/* Red/orange surface gradient - right half */}
        <linearGradient
          id="crpRSurfaceRight"
          x1="146.5"
          y1="43.66"
          x2="302.55"
          y2="43.66"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#ED1C24" />
          <stop offset="0.24" stopColor="#C1272D" />
          <stop offset="0.47" stopColor="#F7931E" />
          <stop offset="0.75" stopColor="#ED1C24" />
          <stop offset="1" stopColor="#F15A24" />
        </linearGradient>
        {/* Top edge gold gradient */}
        <linearGradient
          id="crpRTopGold"
          x1="153.87"
          y1="9.16994"
          x2="153.87"
          y2="19.3599"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        {/* Top edge dark gradient */}
        <linearGradient
          id="crpRTopDark"
          x1="154.66"
          y1="5.95994"
          x2="154.66"
          y2="15.8599"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>
        {/* Left edge gold gradient */}
        <linearGradient
          id="crpRLeftGold"
          x1="9.52982"
          y1="42.7799"
          x2="19.7198"
          y2="42.7799"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        {/* Left edge dark gradient */}
        <linearGradient
          id="crpRLeftDark"
          x1="6.31982"
          y1="42.57"
          x2="16.2098"
          y2="42.57"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>
        {/* Right edge gold gradient */}
        <linearGradient
          id="crpRRightGold"
          x1="298.21"
          y1="42.7799"
          x2="288.02"
          y2="42.7799"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        {/* Right edge dark gradient */}
        <linearGradient
          id="crpRRightDark"
          x1="301.42"
          y1="42.57"
          x2="291.52"
          y2="42.57"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>
        {/* Bottom edge gold gradient */}
        <linearGradient
          id="crpRBottomGold"
          x1="-0.000175636"
          y1="76.4201"
          x2="0.999825"
          y2="66.2301"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        {/* Bottom edge dark gradient */}
        <linearGradient
          id="crpRBottomDark"
          x1="-0.000195775"
          y1="79.6301"
          x2="0.999805"
          y2="69.7301"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#4C4018" />
          <stop offset="1" stopColor="#443427" />
        </linearGradient>
        {/* Corner triangle gradients */}
        <linearGradient
          id="crpRCornerTL"
          x1="16.2098"
          y1="24.4999"
          x2="16.2098"
          y2="4.92994"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        <linearGradient
          id="crpRCornerBL"
          x1="24.8998"
          y1="69.7001"
          x2="5.32982"
          y2="69.7001"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        <linearGradient
          id="crpRCornerBR"
          x1="291.84"
          y1="61.0898"
          x2="291.84"
          y2="80.6498"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        <linearGradient
          id="crpRCornerTR"
          x1="282.98"
          y1="15.8199"
          x2="302.55"
          y2="15.8199"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#876C3D" />
          <stop offset="1" stopColor="#947636" />
        </linearGradient>
        {/* Text clip path */}
        <clipPath id="crpRTextClip">
          <rect x="15" y="5" width="277" height="75" rx="20" />
        </clipPath>
      </defs>

      {/* Layer 1: Dark bottom base */}
      <path
        d="M5.3198 49.7299V75.16C5.3198 82.6 9.2998 89.5499 15.8598 93.07C15.9098 93.0999 15.9598 93.13 16.0198 93.15C23.6198 97.17 30.7898 96.2399 39.4598 95.8399C50.0198 95.3499 60.5898 96.9 71.1598 96.9C92.2298 96.9 113.3 96.93 134.37 96.9C149.52 96.88 164.44 96.9 175.1 96.9C191.44 96.91 199.61 96.92 200.46 96.94C236.36 97.5 239.22 99.93 251.95 96.94C260.75 94.87 263.9 92.6499 272.12 92.7699C279.91 92.8799 284.9 94.9799 291.33 92.3899C296.59 90.27 300.36 85.2799 300.36 85.2799C300.37 85.27 300.38 85.2599 300.39 85.2399C301.92 83.4699 302.72 81.1799 302.7 78.8399C302.59 67.7899 302.49 56.74 302.38 45.7C254.9 52.83 199.77 57.94 138.42 57.9C89.6398 57.87 44.9998 54.5999 5.2998 49.7299H5.3198Z"
        fill="url(#crpRDark)"
      />

      {/* Layer 2: Green mid-layer accent */}
      <path
        d="M5.3198 40.0801V73.64C7.3598 76.66 10.7398 80.7201 16.0198 83.5C23.6198 87.5201 30.7898 86.5901 39.4598 86.1901C50.0198 85.7001 60.5898 87.25 71.1598 87.25C92.2298 87.25 113.3 87.28 134.37 87.25C149.52 87.23 164.44 87.25 175.1 87.25C191.44 87.26 199.61 87.2701 200.46 87.2901C236.36 87.8501 239.22 90.2801 251.95 87.2901C260.75 85.2201 263.9 83.0001 272.12 83.1201C279.91 83.2301 284.9 85.33 291.33 82.74C296.59 80.62 300.36 75.63 300.36 75.63C301.42 74.41 302.2 73.3 302.73 72.49C302.61 60.34 302.5 48.19 302.38 36.05C254.9 43.18 199.77 48.29 138.42 48.25C89.6398 48.22 44.9998 44.9501 5.2998 40.0801H5.3198Z"
        fill="url(#crpRGreen)"
      />

      {/* Layer 3: Red/orange surface - full */}
      <path
        d="M302.55 4.93994V80.5799H16.5398C10.3398 80.5799 5.31982 75.5599 5.31982 69.3599V4.93994H302.55Z"
        fill="url(#crpRSurface)"
      />

      {/* Layer 4: Red/orange surface - left variation */}
      <path
        d="M169.55 40.7199C167.5 43.1999 164.83 44.7499 161.65 45.5899C157.04 46.8299 151.37 46.5899 144.98 45.5899C119.15 41.6099 81.7098 25.4199 56.2198 45.3999C42.9298 55.8099 45.3298 66.2999 34.4698 80.5799H16.5398C10.3398 80.5799 5.31982 75.5599 5.31982 69.3599V36.3399C31.1198 31.1499 39.5698 29.7399 41.9998 23.5599C44.4998 17.2299 38.5598 11.3099 33.2298 4.93994H168.4C174.55 19.1099 175.78 33.1799 169.55 40.7199Z"
        fill="url(#crpRSurfaceLeft)"
      />

      {/* Layer 5: Red/orange surface - right variation */}
      <path
        d="M302.55 6.72998V69.48C286.2 72.85 280.16 74.71 278.16 79.78C278.06 80.05 277.97 80.31 277.89 80.58H146.75C145.94 73.28 147.07 66.9 150.61 62.62C152.66 60.14 155.33 58.59 158.51 57.75C163.12 56.51 168.79 56.75 175.18 57.75C201.01 61.73 238.45 77.92 263.94 57.94C279.87 45.46 273.26 32.86 293.96 13.77C296.53 11.4 299.43 9.01998 302.55 6.72998Z"
        fill="url(#crpRSurfaceRight)"
      />

      {/* White bone decoration - left bones */}
      <path
        opacity="0.4"
        d="M56.7598 60.95L75.2598 51.7C75.2598 51.7 77.7998 45.92 79.6498 48.77C81.4998 51.62 79.6498 51.55 79.6498 51.55C79.6498 51.55 82.4298 51.86 81.8898 53.63C81.3498 55.4 77.4998 54.47 77.4998 54.47L57.9998 63.34C57.9998 63.34 55.6898 67.58 54.9198 65.65C54.1498 63.72 55.9198 63.18 55.9198 63.18C55.9198 63.18 52.2998 63.41 52.9898 61.48C53.6798 59.55 56.7698 60.94 56.7698 60.94L56.7598 60.95Z"
        fill="white"
      />

      {/* White bone decoration - right bones */}
      <path
        opacity="0.4"
        d="M72.2897 61.54L92.8197 64.08C92.8197 64.08 98.1397 60.68 98.0997 64.08C98.0597 67.48 96.5597 66.39 96.5597 66.39C96.5597 66.39 98.6997 68.18 97.2697 69.36C95.8397 70.54 93.1497 67.62 93.1497 67.62L71.9997 64.21C71.9997 64.21 67.7297 66.46 68.1497 64.43C68.5797 62.4 70.3497 62.93 70.3497 62.93C70.3497 62.93 67.2097 61.12 68.8497 59.9C70.4997 58.68 72.2897 61.54 72.2897 61.54Z"
        fill="white"
      />

      {/* White candle decoration - left candle top */}
      <path
        opacity="0.4"
        d="M28.6594 16.8998L31.4294 27.3798C31.4294 27.3798 34.1194 29.2998 32.4594 29.9498C30.7994 30.5898 31.0394 29.6498 31.0394 29.6498C31.0394 29.6498 30.5894 31.0398 29.7294 30.5698C28.8794 30.0998 29.7694 28.2298 29.7694 28.2298L27.2994 17.2798C27.2994 17.2798 25.3694 15.6398 26.4394 15.4498C27.5094 15.2598 27.5994 16.2298 27.5994 16.2298C27.5994 16.2298 27.8694 14.3498 28.7794 14.9098C29.6894 15.4698 28.6494 16.9098 28.6494 16.9098L28.6594 16.8998Z"
        fill="white"
      />

      {/* White candle decoration - left candle mid */}
      <path
        opacity="0.4"
        d="M26.8695 27.3599L27.8595 36.1599C27.8595 36.1599 29.8095 38.0299 28.3895 38.3599C26.9695 38.6899 27.2695 37.9499 27.2695 37.9499C27.2695 37.9499 26.7395 39.0199 26.0995 38.5399C25.4595 38.0599 26.4095 36.6499 26.4095 36.6499L25.7095 27.5099C25.7095 27.5099 24.3495 25.9599 25.2295 25.9299C26.1095 25.8999 26.0695 26.6999 26.0695 26.6999C26.0695 26.6999 26.5095 25.2099 27.1795 25.7699C27.8495 26.3299 26.8395 27.3699 26.8395 27.3699L26.8695 27.3599Z"
        fill="white"
      />

      {/* White candle decoration - left candle bottom */}
      <path
        opacity="0.4"
        d="M19.4796 26.8999L22.5496 18.5999C22.5496 18.5999 21.6496 16.0499 23.0696 16.3999C24.4796 16.7499 23.8796 17.2699 23.8796 17.2699C23.8796 17.2699 24.8396 16.5499 25.1896 17.2699C25.5396 17.9899 24.0596 18.8199 24.0596 18.8199L20.5696 27.2999C20.5696 27.2999 21.0896 29.2999 20.2796 28.9199C19.4796 28.5399 19.8696 27.8599 19.8696 27.8599C19.8696 27.8599 18.8096 28.9899 18.4596 28.1899C18.1096 27.3899 19.4796 26.9199 19.4796 26.9199V26.8999Z"
        fill="white"
      />

      {/* White candle decoration - right */}
      <path
        opacity="0.4"
        d="M290.57 54.5699C287.48 49.9099 279.55 49.1699 276.01 53.2699C273.16 56.5699 273.62 62.4899 276.04 64.4299C276.83 65.0599 277.95 66.4999 277.95 66.4999C277.95 66.4999 277.71 69.1999 277.22 71.1499C277.65 71.2199 278.07 71.2899 278.5 71.3499L278.97 69.4599L279.61 69.1499L279.04 71.4399C279.52 71.5199 280 71.5899 280.48 71.6699L281.04 69.4399L281.71 69.1199L281.05 71.7699C281.56 71.8499 282.08 71.9299 282.59 72.0099L283.17 69.6699L283.81 69.3599L283.13 72.0999C283.73 72.1999 284.32 72.2899 284.92 72.3899C285.07 70.9599 285.21 69.5299 285.36 68.0899C286.06 68.1099 286.75 68.0099 287.42 67.7299C291.13 66.1599 293.6 59.1399 290.58 54.5799L290.57 54.5699ZM282.75 60.9999C281.66 61.8599 277.81 60.1999 277.64 57.8299C277.54 56.4799 278.64 54.8199 279.8 54.7899C280.55 54.7699 281.12 55.4299 281.51 55.8899C282.89 57.4899 283.66 60.2799 282.74 60.9999H282.75ZM289.61 59.3099C288.4 61.3499 284.22 61.1199 283.63 59.8699C283.13 58.8099 285.06 56.6599 287.01 55.8399C287.57 55.5999 288.37 55.2699 289.03 55.6199C290.06 56.1699 290.3 58.1399 289.61 59.3099Z"
        fill="white"
      />

      {/* Top frame border - shadow */}
      <path
        opacity="0.55"
        d="M5.31982 6.06006H302.4C299.82 8.35006 297.25 10.6401 294.66 12.9301H274.39C271.32 13.4001 266.53 14.3501 261.6 16.3401C256.51 18.3901 253.43 20.6701 251.75 22.1401C250.37 20.8301 248.22 19.1501 244.98 17.5301C239.11 14.6001 232.76 13.4201 229.24 12.9201H178.73C175.73 13.4301 171.47 14.3601 167.02 16.0701C162.13 17.9601 158.97 20.0401 157.09 21.4901C155.4 20.1701 152.77 18.4401 148.86 16.8501C142.71 14.3401 136.51 13.4101 132.96 13.0401C116.94 12.8701 103.52 12.9301 93.2498 13.0401C89.5598 13.0801 84.8298 13.1601 79.3698 14.4001C70.9498 16.3201 65.9098 19.8301 63.2298 22.1401C60.6898 20.4301 52.7798 15.5301 39.0198 13.7201C29.8598 12.5101 22.1298 13.1901 18.0298 13.7201C13.7898 11.1701 9.55982 8.62006 5.31982 6.07006V6.06006Z"
        fill="black"
      />

      {/* Top frame border - gold */}
      <path
        d="M5.31982 4.93994H302.4C299.82 7.22994 297.25 9.51994 294.66 11.8099H274.39C271.32 12.2799 266.53 13.2299 261.6 15.2199C256.51 17.2699 253.43 19.5499 251.75 21.0199C250.37 19.7099 248.22 18.0299 244.98 16.4099C239.11 13.4799 232.76 12.2999 229.24 11.7999H178.73C175.73 12.3099 171.47 13.2399 167.02 14.9499C162.13 16.8399 158.97 18.9199 157.09 20.3699C155.4 19.0499 152.77 17.3199 148.86 15.7299C142.71 13.2199 136.51 12.2899 132.96 11.9199C116.94 11.7499 103.52 11.8099 93.2498 11.9199C89.5598 11.9599 84.8298 12.0399 79.3698 13.2799C70.9498 15.1999 65.9098 18.7099 63.2298 21.0199C60.6898 19.3099 52.7798 14.4099 39.0198 12.5999C29.8598 11.3899 22.1298 12.0699 18.0298 12.5999C13.7898 10.0499 9.55982 7.49994 5.31982 4.94994V4.93994Z"
        fill="url(#crpRTopGold)"
      />

      {/* Top frame border - dark */}
      <path
        d="M10.2998 4.93994H299.04C297.59 6.22994 296.13 7.51994 294.68 8.80994H274.41C271.34 9.27994 266.55 10.2299 261.62 12.2199C256.53 14.2699 253.45 16.5499 251.77 18.0299C250.39 16.7199 248.24 15.0399 245 13.4199C239.13 10.4899 232.78 9.30994 229.26 8.80994H178.75C175.75 9.31994 171.49 10.2499 167.04 11.9599C162.15 13.8499 158.99 15.9299 157.11 17.3799C155.42 16.0599 152.79 14.3299 148.88 12.7399C142.73 10.2299 136.53 9.29994 132.98 8.92994C116.96 8.75994 103.54 8.80994 93.2698 8.92994C89.5798 8.96994 84.8498 9.04994 79.3898 10.2899C70.9698 12.2099 65.9298 15.7199 63.2498 18.0299C60.7098 16.3199 52.7998 11.4199 39.0398 9.60994C29.8798 8.39994 22.1498 9.07994 18.0498 9.60994C15.4698 8.05994 12.8898 6.49994 10.3098 4.94994L10.2998 4.93994Z"
        fill="url(#crpRTopDark)"
      />

      {/* Left frame border - shadow */}
      <path
        opacity="0.55"
        d="M6.43994 80.6499V4.93994C8.72994 5.59994 11.0199 6.24994 13.3099 6.90994V12.0699C13.7799 12.8499 14.7299 14.0699 16.7199 15.3299C18.7699 16.6299 21.0499 17.4099 22.5199 17.8399C21.2099 18.1899 19.5299 18.7399 17.9099 19.5699C14.9799 21.0699 13.7999 22.6799 13.2999 23.5799V36.4499C13.8099 37.2199 14.7399 38.2999 16.4499 39.4399C18.3399 40.6899 20.4199 41.4899 21.8699 41.9699C20.5499 42.3999 18.8199 43.0699 17.2299 44.0699C14.7199 45.6399 13.7899 47.2199 13.4199 48.1199C13.2499 52.1999 13.3099 55.6199 13.4199 58.2399C13.4599 59.1799 13.5399 60.3899 14.7799 61.7799C16.6999 63.9299 20.2099 65.2099 22.5199 65.8899C20.8099 66.5399 15.9099 68.5499 14.0999 72.0599C12.8899 74.3899 13.5699 76.3599 14.0999 77.4099C11.5499 78.4899 8.99994 79.5699 6.44994 80.6499H6.43994Z"
        fill="black"
      />

      {/* Left frame border - gold */}
      <path
        d="M5.31982 80.6499V4.93994C7.60982 5.59994 9.89982 6.24994 12.1898 6.90994V12.0699C12.6598 12.8499 13.6098 14.0699 15.5998 15.3299C17.6498 16.6299 19.9298 17.4099 21.3998 17.8399C20.0898 18.1899 18.4098 18.7399 16.7898 19.5699C13.8598 21.0699 12.6798 22.6799 12.1798 23.5799V36.4499C12.6898 37.2199 13.6198 38.2999 15.3298 39.4399C17.2198 40.6899 19.2998 41.4899 20.7498 41.9699C19.4298 42.3999 17.6998 43.0699 16.1098 44.0699C13.5998 45.6399 12.6698 47.2199 12.2998 48.1199C12.1298 52.1999 12.1898 55.6199 12.2998 58.2399C12.3398 59.1799 12.4198 60.3899 13.6598 61.7799C15.5798 63.9299 19.0898 65.2099 21.3998 65.8899C19.6898 66.5399 14.7898 68.5499 12.9798 72.0599C11.7698 74.3899 12.4498 76.3599 12.9798 77.4099C10.4298 78.4899 7.87982 79.5699 5.32982 80.6499H5.31982Z"
        fill="url(#crpRLeftGold)"
      />

      {/* Left frame border - dark */}
      <path
        d="M5.31982 79.38V5.80005C6.60982 6.17005 7.89982 6.54005 9.18982 6.91005V12.07C9.65982 12.85 10.6098 14.07 12.5998 15.33C14.6498 16.63 16.9298 17.41 18.4098 17.84C17.0998 18.19 15.4198 18.74 13.7998 19.57C10.8698 21.07 9.68982 22.68 9.18982 23.58V36.4501C9.69982 37.2201 10.6298 38.3 12.3398 39.44C14.2298 40.69 16.3098 41.4901 17.7598 41.9701C16.4398 42.4001 14.7098 43.07 13.1198 44.07C10.6098 45.64 9.67982 47.22 9.30982 48.12C9.13982 52.2001 9.18982 55.6201 9.30982 58.2401C9.34982 59.18 9.42982 60.39 10.6698 61.78C12.5898 63.93 16.0998 65.21 18.4098 65.89C16.6998 66.5401 11.7998 68.55 9.98982 72.06C8.77982 74.39 9.45982 76.36 9.98982 77.41C8.43982 78.0701 6.87982 78.73 5.32982 79.38H5.31982Z"
        fill="url(#crpRLeftDark)"
      />

      {/* Right frame border - shadow */}
      <path
        opacity="0.55"
        d="M301.28 80.6499V4.93994C298.99 5.59994 296.7 6.24994 294.41 6.90994V12.0699C293.94 12.8499 292.99 14.0699 291 15.3299C288.95 16.6299 286.67 17.4099 285.2 17.8399C286.51 18.1899 288.19 18.7399 289.81 19.5699C292.74 21.0699 293.92 22.6799 294.42 23.5799V36.4499C293.91 37.2199 292.98 38.2999 291.27 39.4399C289.38 40.6899 287.3 41.4899 285.85 41.9699C287.17 42.3999 288.9 43.0699 290.49 44.0699C293 45.6399 293.93 47.2199 294.3 48.1199C294.47 52.1999 294.41 55.6199 294.3 58.2399C294.26 59.1799 294.18 60.3899 292.94 61.7799C291.02 63.9299 287.51 65.2099 285.2 65.8899C286.91 66.5399 291.81 68.5499 293.62 72.0599C294.83 74.3899 294.15 76.3599 293.62 77.4099C296.17 78.4899 298.72 79.5699 301.27 80.6499H301.28Z"
        fill="black"
      />

      {/* Right frame border - gold */}
      <path
        d="M302.41 80.6499V4.93994C300.12 5.59994 297.83 6.24994 295.54 6.90994V12.0699C295.07 12.8499 294.12 14.0699 292.13 15.3299C290.08 16.6299 287.8 17.4099 286.33 17.8399C287.64 18.1899 289.32 18.7399 290.94 19.5699C293.87 21.0699 295.05 22.6799 295.55 23.5799V36.4499C295.04 37.2199 294.11 38.2999 292.4 39.4399C290.51 40.6899 288.43 41.4899 286.98 41.9699C288.3 42.3999 290.03 43.0699 291.62 44.0699C294.13 45.6399 295.06 47.2199 295.43 48.1199C295.6 52.1999 295.54 55.6199 295.43 58.2399C295.39 59.1799 295.31 60.3899 294.07 61.7799C292.15 63.9299 288.64 65.2099 286.33 65.8899C288.04 66.5399 292.94 68.5499 294.75 72.0599C295.96 74.3899 295.28 76.3599 294.75 77.4099C297.3 78.4899 299.85 79.5699 302.4 80.6499H302.41Z"
        fill="url(#crpRRightGold)"
      />

      {/* Right frame border - dark */}
      <path
        d="M302.41 79.38V5.80005C301.12 6.17005 299.83 6.54005 298.54 6.91005V12.07C298.07 12.85 297.12 14.07 295.13 15.33C293.08 16.63 290.8 17.41 289.32 17.84C290.63 18.19 292.31 18.74 293.93 19.57C296.86 21.07 298.04 22.68 298.54 23.58V36.4501C298.03 37.2201 297.1 38.3 295.39 39.44C293.5 40.69 291.42 41.4901 289.97 41.9701C291.29 42.4001 293.02 43.07 294.61 44.07C297.12 45.64 298.05 47.22 298.42 48.12C298.59 52.2001 298.54 55.6201 298.42 58.2401C298.38 59.18 298.3 60.39 297.06 61.78C295.14 63.93 291.63 65.21 289.32 65.89C291.03 66.5401 295.93 68.55 297.74 72.06C298.95 74.39 298.27 76.36 297.74 77.41C299.29 78.0701 300.85 78.73 302.4 79.38H302.41Z"
        fill="url(#crpRRightDark)"
      />

      {/* Bottom frame border - shadow */}
      <path
        opacity="0.55"
        d="M5.31982 79.5299H302.4C299.82 77.2399 297.25 74.95 294.66 72.66H274.39C271.32 72.19 266.53 71.24 261.6 69.25C256.51 67.2 253.43 64.92 251.75 63.45C250.37 64.76 248.22 66.4399 244.98 68.0599C239.11 70.9899 232.76 72.1699 229.24 72.6699H178.73C175.73 72.1599 171.47 71.2299 167.02 69.5199C162.13 67.6299 158.97 65.5499 157.09 64.0999C155.4 65.4199 152.77 67.1499 148.86 68.7399C142.71 71.2499 136.51 72.18 132.96 72.55C116.94 72.72 103.52 72.66 93.2498 72.55C89.5598 72.51 84.8298 72.43 79.3698 71.19C70.9498 69.27 65.9098 65.76 63.2298 63.45C60.6898 65.16 52.7798 70.06 39.0198 71.87C29.8598 73.08 22.1298 72.4 18.0298 71.87C13.7898 74.42 9.55982 76.9699 5.31982 79.5199V79.5299Z"
        fill="black"
      />

      {/* Bottom frame border - gold */}
      <path
        d="M5.31982 80.6501H302.4C299.82 78.3601 297.25 76.0701 294.66 73.7801H274.39C271.32 73.3101 266.53 72.3601 261.6 70.3701C256.51 68.3201 253.43 66.0401 251.75 64.5701C250.37 65.8801 248.22 67.5601 244.98 69.1801C239.11 72.1101 232.76 73.2901 229.24 73.7901H178.73C175.73 73.2801 171.47 72.3501 167.02 70.6401C162.13 68.7501 158.97 66.6701 157.09 65.2201C155.4 66.5401 152.77 68.2701 148.86 69.8601C142.71 72.3701 136.51 73.3001 132.96 73.6701C116.94 73.8401 103.52 73.7801 93.2498 73.6701C89.5598 73.6301 84.8298 73.5501 79.3698 72.3101C70.9498 70.3901 65.9098 66.8801 63.2298 64.5701C60.6898 66.2801 52.7798 71.1801 39.0198 72.9901C29.8598 74.2001 22.1298 73.5201 18.0298 72.9901C13.7898 75.5401 9.55982 78.0901 5.31982 80.6401V80.6501Z"
        fill="url(#crpRBottomGold)"
      />

      {/* Bottom frame border - dark */}
      <path
        d="M10.2998 80.6501H299.04C297.59 79.3601 296.13 78.0701 294.68 76.7801H274.41C271.34 76.3101 266.55 75.3601 261.62 73.3701C256.53 71.3201 253.45 69.0401 251.77 67.5601C250.39 68.8701 248.24 70.5501 245 72.1701C239.13 75.1001 232.78 76.2801 229.26 76.7801H178.75C175.75 76.2701 171.49 75.3401 167.04 73.6301C162.15 71.7401 158.99 69.6601 157.11 68.2101C155.42 69.5301 152.79 71.2601 148.88 72.8501C142.73 75.3601 136.53 76.2901 132.98 76.6601C116.96 76.8301 103.54 76.7801 93.2698 76.6601C89.5798 76.6201 84.8498 76.5401 79.3898 75.3001C70.9698 73.3801 65.9298 69.8701 63.2498 67.5601C60.7098 69.2701 52.7998 74.1701 39.0398 75.9801C29.8798 77.1901 22.1498 76.5101 18.0498 75.9801C15.4698 77.5301 12.8898 79.0901 10.3098 80.6401L10.2998 80.6501Z"
        fill="url(#crpRBottomDark)"
      />

      {/* Corner triangle - top left fill */}
      <path
        d="M5.31982 4.93994H17.2598C17.3198 5.03994 17.3698 5.14994 17.4398 5.26994C19.9398 10.0099 27.0898 24.5099 27.0898 24.5099L5.31982 19.0899V4.93994Z"
        fill="#72531C"
      />

      {/* Corner triangle - top left gradient */}
      <path
        d="M27.0898 24.5099L5.31982 4.93994V19.0899L27.0898 24.5099Z"
        fill="url(#crpRCornerTL)"
      />

      {/* Corner triangle - bottom left fill */}
      <path
        d="M5.31982 80.5801V68.6401C5.41982 68.5801 5.52982 68.5301 5.64982 68.4601C10.3898 65.9601 24.8898 58.8101 24.8898 58.8101L19.4698 80.5801H5.31982Z"
        fill="#72531C"
      />

      {/* Corner triangle - bottom left gradient */}
      <path
        d="M24.8898 58.8101L5.31982 80.5801H19.4798L24.8898 58.8101Z"
        fill="url(#crpRCornerBL)"
      />

      {/* Corner triangle - bottom right fill */}
      <path
        d="M302.73 80.6498H290.79C290.73 80.5498 290.68 80.4398 290.61 80.3198C288.11 75.5798 280.96 61.0798 280.96 61.0798L302.73 66.4998V80.6498Z"
        fill="#72531C"
      />

      {/* Corner triangle - bottom right gradient */}
      <path d="M280.96 61.0798L302.73 80.6498V66.4998L280.96 61.0798Z" fill="url(#crpRCornerBR)" />

      {/* Corner triangle - top right fill */}
      <path
        d="M302.55 4.93994V16.8799C302.45 16.9399 302.34 16.9899 302.22 17.0599C297.48 19.5599 282.98 26.7099 282.98 26.7099L288.4 4.93994H302.55Z"
        fill="#72531C"
      />

      {/* Corner triangle - top right gradient */}
      <path d="M282.98 26.7099L302.55 4.93994H288.4L282.98 26.7099Z" fill="url(#crpRCornerTR)" />

      {/* Dynamic player pseudo text */}
      <g clipPath="url(#crpRTextClip)">
        <text
          x={PSEUDO_BOX_CONFIG.TEXT.RIGHT_X_POSITION}
          y={PSEUDO_BOX_CONFIG.TEXT.Y_POSITION}
          fill="#FFFFFF"
          fontSize={PSEUDO_BOX_CONFIG.TEXT.FONT_SIZE}
          fontWeight="bold"
          textAnchor="end"
          dominantBaseline="middle"
          stroke="#000000"
          strokeWidth="2"
          strokeLinejoin="round"
          paintOrder="stroke"
        >
          {displayText}
        </text>
      </g>
    </svg>
  );
});

export default CryptRightPseudoBox;
